import { ReminderModelName } from 'types/enums/ReminderModelName'
import { parseFilterDate } from 'utils/dateRangePresets'

export interface ReminderModelConfig {
    label: string
    /** Route to the list page */
    listRoute: string
    /** Route to the detail page for a given ID. Null means no detail page. */
    detailRoute: (id: number) => string | null
    /** Tailwind color class for the badge bg */
    colorClass: string
    /** Tailwind text color class for the badge */
    textColorClass: string
}

const REMINDER_MODEL_CONFIG: Record<ReminderModelName, ReminderModelConfig> = {
    [ReminderModelName.Lead]: {
        label: 'Lead',
        listRoute: '/leads',
        detailRoute: () => '/leads',
        colorClass: 'bg-purple-100 dark:bg-purple-900/30',
        textColorClass: 'text-purple-700 dark:text-purple-300',
    },
    [ReminderModelName.Opportunity]: {
        label: 'Opportunity',
        listRoute: '/opportunities',
        detailRoute: () => '/opportunities',
        colorClass: 'bg-blue-100 dark:bg-blue-900/30',
        textColorClass: 'text-blue-700 dark:text-blue-300',
    },
    [ReminderModelName.Offer]: {
        label: 'Offer',
        listRoute: '/offers',
        detailRoute: (id) => `/offers/${id}`,
        colorClass: 'bg-teal-100 dark:bg-teal-900/30',
        textColorClass: 'text-teal-700 dark:text-teal-300',
    },
    [ReminderModelName.Reservation]: {
        label: 'Reservation',
        listRoute: '/reservations',
        detailRoute: (id) => `/reservations/detail/${id}`,
        colorClass: 'bg-green-100 dark:bg-green-900/30',
        textColorClass: 'text-green-700 dark:text-green-300',
    },
    [ReminderModelName.OnlineReservation]: {
        label: 'Online Reservation',
        listRoute: '/online-reservations',
        detailRoute: () => '/online-reservations',
        colorClass: 'bg-emerald-100 dark:bg-emerald-900/30',
        textColorClass: 'text-emerald-700 dark:text-emerald-300',
    },
    [ReminderModelName.Company]: {
        label: 'Company',
        listRoute: '/companies',
        detailRoute: () => '/companies',
        colorClass: 'bg-orange-100 dark:bg-orange-900/30',
        textColorClass: 'text-orange-700 dark:text-orange-300',
    },
    [ReminderModelName.Contact]: {
        label: 'Contact',
        listRoute: '/contact_companies/list',
        detailRoute: (id) => `/contact_companies/detail/${id}`,
        colorClass: 'bg-pink-100 dark:bg-pink-900/30',
        textColorClass: 'text-pink-700 dark:text-pink-300',
    },
    [ReminderModelName.Invoice]: {
        label: 'Invoice',
        listRoute: '/invoices',
        detailRoute: () => '/invoices',
        colorClass: 'bg-yellow-100 dark:bg-yellow-900/30',
        textColorClass: 'text-yellow-700 dark:text-yellow-300',
    },
    [ReminderModelName.AfterSale]: {
        label: 'After Sale',
        listRoute: '/after-sales',
        detailRoute: () => '/after-sales',
        colorClass: 'bg-gray-100 dark:bg-gray-700/50',
        textColorClass: 'text-gray-700 dark:text-gray-300',
    },
    [ReminderModelName.Driver]: {
        label: 'Driver',
        listRoute: '/reminders',
        detailRoute: () => null,
        colorClass: 'bg-cyan-100 dark:bg-cyan-900/30',
        textColorClass: 'text-cyan-700 dark:text-cyan-300',
    },
    [ReminderModelName.Seller]: {
        label: 'Seller',
        listRoute: '/reminders',
        detailRoute: () => null,
        colorClass: 'bg-indigo-100 dark:bg-indigo-900/30',
        textColorClass: 'text-indigo-700 dark:text-indigo-300',
    },
}

const REMINDER_MODEL_NAMES = new Set<ReminderModelName>(Object.values(ReminderModelName))

export const isReminderModelName = (value: string): value is ReminderModelName =>
    REMINDER_MODEL_NAMES.has(value as ReminderModelName)

export const normalizeReminderModelName = (modelName: string | null | undefined): ReminderModelName | null => {
    if (!modelName) {
        return null
    }

    const normalized = modelName.trim().toLowerCase()
    // Read legacy reminders while keeping all new contact writes canonical.
    if (normalized === 'contact') return ReminderModelName.Contact
    return isReminderModelName(normalized) ? normalized : null
}

/**
 * Backend Reminder resolves model_name to an Eloquent class.
 * Opportunities are Lead records and contacts use the company_contact morph alias.
 */
export const toApiReminderModelName = (
    modelName: ReminderModelName | string | null | undefined
): string | null | undefined => {
    if (modelName == null) {
        return modelName
    }

    const normalized = String(modelName).trim().toLowerCase()
    if (normalized === 'contact' || normalized === ReminderModelName.Contact) {
        return ReminderModelName.Contact
    }
    if (normalized === ReminderModelName.Opportunity || normalized === 'opportunity') {
        return ReminderModelName.Lead
    }

    return typeof modelName === 'string' ? modelName : String(modelName)
}

export const getReminderModelConfig = (modelName: string): ReminderModelConfig | null => {
    const normalizedModelName = normalizeReminderModelName(modelName)
    if (!normalizedModelName) {
        return null
    }
    return REMINDER_MODEL_CONFIG[normalizedModelName] ?? null
}

export const getReminderModelRoute = (modelName: string, modelId: number | null): string | null => {
    const config = getReminderModelConfig(modelName)
    if (!config || modelId == null) return null
    return config.detailRoute(modelId)
}

export const REMINDER_TYPE_VALUES = ['follow_up', 'call', 'email', 'meeting'] as const

export type ReminderTypeValue = (typeof REMINDER_TYPE_VALUES)[number]

export const getReminderTypeFilterOptions = (t: (key: string) => string) =>
    REMINDER_TYPE_VALUES.map((value) => ({
        label: t(value),
        value,
    }))

export const REMINDERS_TABLE_SORT_MAP: Record<string, string> = {
    reminder_type: 'type',
    entity: 'model_name',
}

export const transformRemindersSorting = (
    sorting: { id: string; desc: boolean }[]
): { id: string; desc: boolean }[] =>
    sorting.map((sort) => ({
        ...sort,
        id: REMINDERS_TABLE_SORT_MAP[sort.id] ?? sort.id,
    }))

export const transformRemindersSearch = (
    filters: { id: string; value: unknown }[] | { id: string; value: unknown } | undefined
): { id: string; value: unknown }[] => {
    const list = Array.isArray(filters) ? filters : filters ? [filters] : []

    return list.flatMap((filter) => {
        if (!filter?.id) {
            return []
        }

        const searchId = filter.id === 'reminder_type' ? 'type' : filter.id
        if (searchId === 'due_at' && filter.value && typeof filter.value === 'object' && 'from' in filter.value && 'to' in filter.value) {
            const range = filter.value as {from: string; to: string}
            const from = parseFilterDate(range.from)
            const to = parseFilterDate(range.to)
            if (!from || !to) return []
            return [{id: searchId, value: {
                from: from.startOf('day').format('YYYY-MM-DD HH:mm:ss.SSS'),
                to: to.endOf('day').format('YYYY-MM-DD HH:mm:ss.SSS'),
            }}]
        }

        if ((filter.id === 'type' || filter.id === 'reminder_type') && Array.isArray(filter.value)) {
            if (filter.value.length === 0) {
                return []
            }

            if (filter.value.length === 1) {
                return [{ id: searchId, value: filter.value[0] }]
            }

            return [{ id: searchId, value: filter.value }]
        }

        return [{ id: searchId, value: filter.value }]
    })
}

export const ALL_REMINDER_MODEL_NAMES = Object.values(ReminderModelName)
